import { MapPin, Clock, Navigation } from 'lucide-react';
import Button from '../ui/Button';
import { useLanguage } from '../../context/LanguageContext';
import { getFormattedHours, isCurrentlyOpen } from '../../data/openingHours';

export default function LocationHours() {
  const { t, language } = useLanguage();
  const hours = getFormattedHours(language);
  const isOpen = isCurrentlyOpen();

  return (
    <section className="py-16 md:py-24 bg-white dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('location.title')}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('location.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Map Placeholder */}
          <div className="relative rounded-2xl overflow-hidden bg-cream-200 dark:bg-gray-700 aspect-video lg:aspect-square">
            <img
              src="https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800&h=600&fit=crop"
              alt="Munich map area"
              className="w-full h-full object-cover opacity-50"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
              <MapPin className="w-12 h-12 text-primary-600 dark:text-primary-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Maximilianstraße 45
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                80539 München
              </p>
              <Button
                href="https://maps.google.com/?q=Maximilianstraße+45+München"
                target="_blank"
                rel="noopener noreferrer"
                variant="secondary"
                size="sm"
              >
                <Navigation className="w-4 h-4" />
                {t('location.getDirections')}
              </Button>
            </div>
          </div>

          {/* Opening Hours */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              <h3 className="text-2xl font-display font-semibold text-gray-900 dark:text-white">
                {t('location.hours')}
              </h3>
              {/* Open/Closed badge */}
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isOpen
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}
              >
                {isOpen ? t('header.openNow') : t('header.closed')}
              </span>
            </div>

            <div className="space-y-3">
              {hours.map((item, index) => (
                <div
                  key={index}
                  className={`flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                    item.closed ? 'opacity-60' : ''
                  }`}
                >
                  <span className="font-medium text-gray-900 dark:text-white">
                    {item.day}
                  </span>
                  <span
                    className={
                      item.closed
                        ? 'text-wine-600 dark:text-wine-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }
                  >
                    {item.hours}
                  </span>
                </div>
              ))}
            </div>

            {/* Contact info */}
            <div className="mt-8 p-4 bg-cream-100 dark:bg-gray-700 rounded-xl">
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                {t('contactPage.info.phone')}:
              </p>
              <a
                href="tel:+4989987654"
                className="text-xl font-semibold text-primary-700 dark:text-primary-400 hover:text-primary-600"
              >
                089 - 987 654 32
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
